/* v8 ignore file -- @preserve */
// EX-footerStore
import { defineStore } from 'pinia';
import { SET_FETCH_ERROR, SET_FOOTER_DATA, DELETE_FOOTER_LINKS } from '#shared/types/actions';
import {
  GET_COPYRIGHT,
  GET_CREDIT_CARD_DATA,
  GET_FOOTER_DATA,
  GET_FOOTER_LINKS,
  GET_LEGAL_LINKS,
  GET_SIGNUP_DATA,
  GET_SOCIAL_LINKS,
} from '#shared/types/getters';
import { useFooterFetch } from '~/composables/dumb/useNavigationFetch';
import { filterAemFooterLink, filterAemLegalLink, mapAemFooterLink, mapAemLegalLink } from '~/utils/footerUtils';
import transformEditorPopupLink from '~/utils/transformEditorPopupLink';
import { loadFooterMock } from '#shared/utils/footerHelpers';

export const useNavFooter = defineStore('navFooter', {
  state: () => ({
    data: null,
    isFetchError: false,
  }),
  getters: {
    isCached: (state) => !!state.data,

    [GET_FOOTER_DATA]: (state) => state.data,

    [GET_FOOTER_LINKS](state) {
      const footerLinks = state.data?.footer?.footerLinks;
      if (!footerLinks) return [];

      return footerLinks.filter(filterAemFooterLink).map(mapAemFooterLink);
    },

    [GET_COPYRIGHT](state) {
      return state.data?.footer?.copyright?.copyrightText || '';
    },

    [GET_LEGAL_LINKS](state) {
      const legalLinks = state.data?.footer?.legalLinks;
      return legalLinks ? legalLinks.filter(filterAemLegalLink).map(mapAemLegalLink) : [];
    },

    [GET_SOCIAL_LINKS](state) {
      const links = state.data?.footer?.socialPanel?.socialIcons?.contentlinks || [];

      return links.map(({ text, value, tracking }) => {
        const title = text.split('-')[1] || '';
        let normalizedTitle = '';
        let alt = '';

        if (title) {
          normalizedTitle = title[0].toUpperCase() + title.slice(1);

          alt = title === 'mobile' ? 'Go to our Mobile page' : `Visit us on ${normalizedTitle}`;
        }

        return {
          title: normalizedTitle,
          link: value,
          icon: text,
          alt,
          tracking,
        };
      });
    },
    [GET_CREDIT_CARD_DATA](state) {
      const creditCard = state.data?.footer?.socialPanel?.cardSignUp;

      if (!creditCard) return null;

      const { heading = '', body: content = '', contentlinks: actions = [] } = creditCard;

      return {
        heading,
        content,
        actions,
      };
    },
    [GET_SIGNUP_DATA](state) {
      const { isMcom } = useEnvProps();

      if (isMcom) {
        return state.data?.footer?.socialPanel?.emailSignUp;
      }

      const ads = state.data?.footer?.ads;

      if (!ads) return {};

      return ads.reduce((map, { type, text }) => {
        map[type] = transformEditorPopupLink(text);
        return map;
      }, {});
    },
  },
  actions: {
    [SET_FOOTER_DATA]({ data }) {
      delete data.meta; // remove meta to reduce memory usage and SSR response size as we are not using it in footer
      this.data = data;
    },

    [SET_FETCH_ERROR](flag) {
      this.isFetchError = flag;
    },

    [DELETE_FOOTER_LINKS]() {
      const data = this.data?.footer;

      if (!data) return;

      delete data.footerLinks;
      delete data.legalLinks;
    },

    async fetch(force = false) {
      if (!force && this.isCached) {
        return;
      }

      const pageData = usePageData();
      const envProps = useEnvProps();
      const { context, queryParams: serverQueryParams = {} } = pageData.navigation;
      const { navMock: isMockMode } = serverQueryParams;
      const { hostlink: clientBaseUrl, isMcom } = envProps;

      let data;
      let isFetchSucceed = false;
      // nice to know: the footer data is the same for mobile and desktop
      // so its not required passing deviceType
      if (!isMockMode) {
        try {
          const result = await useFooterFetch({
            ...context,
            clientBaseUrl,
          });
          isFetchSucceed = result && typeof result === 'object';
          if (isFetchSucceed) {
            data = result;
            this[SET_FETCH_ERROR](false);
          }
        } catch (error) {
          // Fetch failed, isFetchSucceed remains false
          console.error('useFooterFetch error', error);
        }
      }

      if (!isFetchSucceed) {
        data = await loadFooterMock({
          isInternational: context.isInternational,
          isMobile: false,
          isMcom,
        });
        this[SET_FETCH_ERROR](true);
        envProps.isNavMockFooter = true;
      }

      this[SET_FOOTER_DATA]({ data });
    },
  },
});
